import { DOCUMENT, formatDate } from '@angular/common';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { GetContextMenuItemsParams, GridOptions, SuppressKeyboardEventParams } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { DashboardNavigationService } from 'src/app/navigation/dashboard-navigation.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { CvAngularLoadingOverlayComponent } from 'src/app/reusables/ag-grid-components/cv-angular-loading-overlay/cv-angular-loading-overlay.component';
import { CvDialogService } from 'src/app/reusables/confirm-dialog/cv-dialog.service';
import { FeaturesService } from 'src/app/reusables/features.service';
import { BoldTextPipe } from 'src/app/reusables/pipes/bold-text.pipe';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { DashboardResponsiveApiModelSparse, WorkspaceNewTabViewMode, WorkspaceTemplateViewMode } from 'src/shared/models/dashboard.model';
import { DashboardService } from '../../dashboard.service';
import { ShareViaUrlComponent } from '../../modals/share-via-url/share-via-url.component';
import { ShareViaUrlService } from '../../modals/share-via-url/share-via-url.service';
import { TakeOwnershipService } from '../../modals/take-ownership/take-ownership.service';
import { SystemTemplateService } from '../../system-templates/system-template.service';
import { ActionsCellRendererComponent } from './ag-grid-components/actions-cell-renderer/actions-cell-renderer.component';
import { CxoneDashboardNameCellRendererComponent } from './ag-grid-components/cxone-dashboard-name-cell-renderer/cxone-dashboard-name-cell-renderer.component';
import { DashboardNameRendererComponent } from './ag-grid-components/dashboard-name-renderer/dashboard-name-renderer.component';
import { DescriptionCellRendererComponent } from './ag-grid-components/description-renderer/description-renderer.component';
import { FavoriteButtonRendererComponent } from './ag-grid-components/favorite-button-renderer/favorite-button-renderer.component';
import { OwnerCellRendererComponent } from './ag-grid-components/owner-cell-renderer/owner-cell-renderer.component';
import { ShareTextRendererComponent } from './ag-grid-components/share-text-renderer/share-text-renderer/share-text-renderer.component';

@Component({
    selector: 'cv-manage-available-dashboards',
    templateUrl: './manage-available-dashboards.component.html',
    styleUrls: ['./manage-available-dashboards.component.scss']
})
export class ManageAvailableDashboardsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() allowManagement = false;
    @Input() multiSelect = true;
    @Input() toolbar = true;
    @Input() searchbar = false;
    @Input() searchFilter = '';
    @Input() minSearchLength = 1;
    @Input() favoritesFilter = false;
    @Input() columns = ['DashboardName', 'DashboardGroup', 'LastViewDate', 'LastModifiedDate', 'Actions']; // the colIds of the desired AG Grid columnDefs
    @Input() allCapsHeaders = false;
    @Input() showCurrentlyOpen = true;
    @Input() isCXOne = false; // whether this is the CXOne Dashboard Management page

    @Output() selectionChanged = new EventEmitter<DashboardResponsiveApiModelSparse[]>();
    @Output() shareClicked = new EventEmitter<DashboardResponsiveApiModelSparse[]>();
    @Output() copyClicked = new EventEmitter<DashboardResponsiveApiModelSparse>();
    @Output() editDetailsClicked = new EventEmitter<DashboardResponsiveApiModelSparse>();
    @Output() subscribeClicked = new EventEmitter<DashboardResponsiveApiModelSparse[]>();
    @Output() numberOfGridRows = new EventEmitter<number>();

    @ViewChild('moveDashboardDropdown', { read: NgbDropdown }) moveDashboardDropdown: NgbDropdown;
    @ViewChild('ownershipErrorModal', { read: TemplateRef }) ownershipErrorModal: TemplateRef<any>;
    @ViewChild('cxoneDeleteModal', { read: TemplateRef}) cxoneDeleteModal: TemplateRef<any>;
    @ViewChild('manageAvailableDashboardsGrid') agGrid: AgGridAngular;

    isLoading = false;
    hasSystemTemplateAccess = false;
    gridOptions: GridOptions = null;
    selectedDashboards: DashboardResponsiveApiModelSparse[] = [];
    showSearch = false;
    moveDashboardDestination = '';
    isMoving = false;
    localeText = {}; // object for ag grid localization
    dashboardFilters: string[] = [];
    selectedDashboardFilter = 0;

    // action tooltips and status
    canMoveSelected = false;
    canDeleteSelected = false;
    canShareSelected = false;
    canSubscribeSelected = false;
    canOpenSelected = false;
    hasTakeOwnershipPermission = false;
    hasReadPermission = false;
    hasCreatePermission = false;
    hasCreateSystemTemplatePermission = false;
    canCreateSystemTemplates = false;
    onMainDashboardPage = false;

    moveSelectedTooltip = '';
    copySelectedTooltip = '';
    deleteSelectedTooltip = '';
    shareSelectedTooltip = '';
    subscribeSelectedTooltip = '';
    openSelectedTooltip = '';
    systemTemplateTooltip = '';

    subscriptionsFeatureEnabled = false;

    contextUtil: ContextUtil = {
        hasWriteAccess: (dashboard: DashboardResponsiveApiModelSparse) => this.hasWriteAccess(dashboard) && this.hasCreatePermission,
        hasReadAccess: (dashboard: DashboardResponsiveApiModelSparse) => this.hasReadAccess(dashboard) && this.hasReadPermission,
        isDashboardAbandoned: (dashboardKey: string) => this.dashboardsIsAbandoned.get(dashboardKey),
        deleteDashboard: () => this.deleteSelectedDashboards(),
        shareDashboard: (dashboard: DashboardResponsiveApiModelSparse) => this.shareClicked.emit([dashboard]),
        setFavorite: (dashboardID: number, value: boolean) => this.setFavorite(dashboardID, value),
        getAddFavoriteTooltip: () => this.getLocaleText("Favorite_Tooltip_Add", "Add to favorites"),
        getRemoveFavoriteTooltip: () => this.getLocaleText("Favorite_Tooltip_Remove", "Remove from favorites"),
        getDeleteTooltip: () => this.getLocaleText("Delete_Tooltip", "Delete dashboard"),
        getFilterText: () => this.searchFilter,
    };

    private dashboards: DashboardResponsiveApiModelSparse[];
    private abandonedDashboards: DashboardResponsiveApiModelSparse[];
    private dashboardIndices: Map<string, number> = new Map();
    private openDashboardIndices: Map<string, number> = new Map();
    private dashboardsIsAbandoned: Map<string, boolean> = new Map();

    private _subscriptions: Subscription[] = [];

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        public dashboardService: DashboardService,
        private dashboardNavigationService: DashboardNavigationService,
        private navigationService: NavigationService,
        private dialogService: CvDialogService,
        private systemTemplateService: SystemTemplateService,
        private takeOwnershipService: TakeOwnershipService,
        private shareViaURL: ShareViaUrlService,
        public modalService: NgbModal,
        public boldTextPipe: BoldTextPipe,
        private featureService: FeaturesService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.getLocaleText = cvLocalizationService.getCachedText.bind(cvLocalizationService, "Dashboards_Landing");

        this._subscriptions.push(this.dashboardService.availableDashboardsChanged.subscribe(() => {
            this.initializeDashboards();
        }));
    }

    get hasOwnLegacyDashboard() { return this.dashboardNavigationService.hasOwnLegacyDashboard; };

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Dashboards_Landing");
        this.localeText = await this.agGridHelperService.getLocalizations();

        this.hasCreateSystemTemplatePermission  = await this.systemTemplateService.hasSystemTemplateAdminAccess();

        // Check if the user has access to subscribe to dashboards. If not CXone, this is always true. Otherwise, check the featuresService.
        if (!this.isCXOne)
            this.subscriptionsFeatureEnabled = true;
        else
            this.subscriptionsFeatureEnabled = await this.featureService.isFeatureEnabled('Feature_CXone_Subscriptions');

        if (window['angular'])
            this.onMainDashboardPage = angular.element('dashboard-new-content').length > 0;

        await this.dashboardService.getUserWorkspace();

        this.hasTakeOwnershipPermission = this.isCXOne ? this.dashboardService.permissions['ChangeDashboardOwnership'] : false;
        this.hasCreatePermission = this.isCXOne ?
            this.dashboardService.permissions["launch_cxone_dash"] : this.dashboardService.permissions['DashboardNew'];
        this.hasReadPermission = this.hasCreatePermission ? true : this.dashboardService.permissions['launch_cxone_dash-read']; // only used for the cxone management page
        if (!this.hasCreatePermission && this.allowManagement) {
            this.copySelectedTooltip = this.getLocaleText('Copy_Tooltip_Access', 'You do not have permission to make copies.');
        }

        // this.dashboardFilters = !this.isCXOne ?
        //     [this.getLocaleText("All_Dashboards_Caps", "All Dashboards"),
        //         this.getLocaleText("Shared_Dashboards", "Shared Dashboards"),
        //         this.getLocaleText("Dashboards_Shared_with_Me", "Dashboards Shared with Me")]
        //     : [];

        this.updateManagementState();
        this.initializeGrid();

        if (!this.dashboardService.dashboardsLoading)
            await this.dashboardService.loadExistingDashboardsAndGroups();

        if (this.isCXOne)
            this.document.body.classList.add('cx-one-ui');

    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.hasReadPermission || changes.hasCreatePermission) {
            this.setGridData();
        }

        if (changes.searchFilter || changes.favoritesFilter) {
            this.applyFilter();

            if (changes.favoritesFilter)
                this.updateSort();
        }

        if (changes.allowManagement) {
            this.updateManagementState();
        }
    }

    ngOnDestroy() {
        this._subscriptions.forEach(sub => sub.unsubscribe());
        this.modalService.dismissAll();
    }

    async initializeDashboards() {
        if (this.isCXOne && this.hasReadPermission) {
            this.dashboards = this.dashboardService.availableDashboards.filter(d => d.IsCXOne === true);
            if (this.hasTakeOwnershipPermission)
                await this.loadAbandonedDashboards();
        }
        else
            this.dashboards = this.dashboardService.availableDashboards.filter(d => d.IsCXOne === false);

        if (this.agGrid?.api)
            this.setGridData();

        // update indices
        this.dashboardIndices.clear();
        this.dashboards?.forEach((dashboard, index) => {
            this.dashboardIndices.set(dashboard.DashboardKey, index);
        });

        this.openDashboardIndices.clear();
        this.dashboardService.dashboards?.forEach((dashboard, index) => {
            if (dashboard != null)
                this.openDashboardIndices.set(dashboard.DashboardKey, index);
        });
    }

    /** Loads abandoned dashboards and marks ownership status. Used for CXOne. */
    async loadAbandonedDashboards() {
        this.dashboardsIsAbandoned.clear();
        this.dashboards.forEach((dashboard) => {
            this.dashboardsIsAbandoned.set(dashboard.DashboardKey, false);
        });

        try {
            this.abandonedDashboards = await this.takeOwnershipService.loadInactiveDashboards(); // will be empty if user doesn't have access
        }
        catch (err) {
            this.abandonedDashboards = [];
            return;
        }

        if (this.abandonedDashboards) {
            this.abandonedDashboards = this.abandonedDashboards.filter(d => d.IsCXOne === true);
            this.abandonedDashboards.forEach((abandonedDashboard) => {
                const sharedDash = this.dashboards.find(dashboard => dashboard.RelatedDashboardID === abandonedDashboard.DashboardID);
                if (sharedDash)
                    this.dashboardsIsAbandoned.set(sharedDash.DashboardKey, true);
            });
        }
    }

    initializeGrid() {
        const colDefs = this.populateColumnDefs();

        this.gridOptions = this.agGridHelperService.createGridOptions({
            components: {
                cvAngularLoadingComponent: CvAngularLoadingOverlayComponent,
                dashboardNameRenderer: DashboardNameRendererComponent,
                favoriteButtonRenderer: FavoriteButtonRendererComponent,
                actionsCellRenderer: ActionsCellRendererComponent,
                shareTextRenderer: ShareTextRendererComponent,
                ownerCellRenderer: OwnerCellRendererComponent,
                cxoneDashboardNameCellRenderer: CxoneDashboardNameCellRendererComponent,
                descriptionCellRenderer: DescriptionCellRendererComponent,
            },
            columnDefs: colDefs,
            onGridReady: _ => this.setGridData(),

            // Allows filtering ag-grid using a module's search bar
            isExternalFilterPresent: () => this.searchFilter.length > 0 || this.favoritesFilter || this.dashboardFilters.length > 0,
            doesExternalFilterPass: node => {
                if (this.searchFilter.length > 0){
                    if (!this.agGridHelperService.doesExternalFilterPass(node, this.searchFilter))
                        return false;
                }

                if(this.favoritesFilter){
                    if (!node.data.IsFavorite)
                        return false;
                }

                if(this.dashboardFilters.length > 0 && this.selectedDashboardFilter > 0){
                    if (!this.doesDashboardFilterPass(node))
                        return false;
                }

                return true;
            },
            onFilterChanged: () => {
                if (!this.dashboardService.dashboardsLoading) {
                    if (this.agGrid?.api) {
                        if (this.isCXOne && !this.hasReadPermission) {
                            this.gridOptions.localeText.noRowsToShow = this.getLocaleText(
                                "No_Management_Permission", "You don't have permission to manage dashboards");
                            this.agGrid?.api.showNoRowsOverlay();
                            return;
                        }

                        this.numberOfGridRows.emit(this.agGrid?.api?.getDisplayedRowCount());

                        if (this.agGrid?.api?.getDisplayedRowCount() === 0) {
                            this.updateNoRowsMessage();
                            this.agGrid?.api.showNoRowsOverlay();
                        }
                        else
                            this.agGrid?.api?.hideOverlay();

                        requestAnimationFrame(() =>
                            this.agGrid?.api.redrawRows()
                        );
                    }
                }
            },
            getRowId: ({ data: rowData }) => rowData.DashboardKey,
            onColumnVisible: event => {
                let visibleColumns = 0;
                this.agGrid?.api.getColumns().forEach((c) => {
                    if (c.isVisible()) {
                        visibleColumns++;
                    }
                });
                if (visibleColumns === 2) {
                    this.agGrid?.api.setColumnVisible(this.agGrid?.api.getColumns()[1].getColId(), true);
                }
                if (this.agGrid?.api && !this.isCXOne)
                    event.api.sizeColumnsToFit();
            },
            onGridSizeChanged: params => {
                if (this.agGrid?.api && !this.isCXOne)
                    this.agGrid?.api.sizeColumnsToFit();
            },
            onSelectionChanged: event => {
                this.selectedDashboards = event.api.getSelectedRows();
                this.selectionChanged.emit([...this.selectedDashboards]);
                this.updateActionsStatus();
            },
            getContextMenuItems: params => this.initializeContextMenu(params),
            loadingOverlayComponent: 'cvAngularLoadingComponent',
            suppressCellFocus: !this.isCXOne, // cell selection is required for accessible keyboard controls
            suppressMovableColumns: !this.isCXOne,
            suppressDragLeaveHidesColumns: true,
            suppressContextMenu: true,
            localeText: this.localeText,
            context: { contextUtil: this.contextUtil },
            headerHeight: this.isCXOne ? 45 : null,
            icons: this.isCXOne ? {
                sortAscending: `<div class="sortIcons">
                    <svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg>
                    <svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg>
                </div>`,
                sortDescending: `<div class="sortIcons">
                    <svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg>
                    <svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg>
                </div>`,
                filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
            } : null,
            rowHeight: this.isCXOne ? 36 : null,
            rowClass: this.isCXOne ? 'cxOneRow' : null,
            ensureDomOrder: true,
            suppressHorizontalScroll: !this.isCXOne,
        });
    }

    populateColumnDefs() {
        const availableColumnDefs = [
            {
                field: "DashboardName",
                colId: "DashboardName",
                headerName: this.getLocaleText("Name", "Name"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: this.isCXOne ? 'cxoneDashboardNameCellRenderer' : 'dashboardNameRenderer',
                cellRendererParams: {
                    dashboardClicked: (key: string) => this.openDashboard(key),
                },
                menuTabs: this.isCXOne ? null : [],
                sort: this.isCXOne && !this.favoritesFilter ? 'asc' : null,
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 2,
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
            },
            {
                field: "DashboardGroup",
                colId: "DashboardGroup",
                headerName: this.getLocaleText("Folder", "Folder"),
                valueFormatter: params => {
                    if (params.data["SharedBy"])
                        return this.getLocaleText("Shared", "Shared With Me");

                    return this.sanitizeName(params.value) ?? '-';
                },
                menuTabs: this.isCXOne ? null : [],
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                flex: 1,
            },
            {
                field: "LastViewDate",
                colId: "LastViewDate",
                headerName: this.getLocaleText("Last_Viewed", "Last Viewed"),
                comparator: this.isCXOne ? (dateA, dateB, nodeA, nodeB) => this.compareDateNullLast(dateA, dateB, nodeA, nodeB, 'LastViewDate') : this.compareDateNullFirst,
                cellStyle: params => {
                    if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey))
                        return { 'font-style': 'italic' };
                },
                valueGetter: params => {
                    if (this.isCXOne) {
                        if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey)) {
                            return this.getLocaleText("Currently_Open", "Currently Open");
                        }
                        if (!params.data || !params.data[params.colDef.field]) {
                            return this.isCXOne ? "" : "-";
                        }
                        return this.formatDate(params.data[params.colDef.field]);
                    } else {
                        return params.data[params.colDef.field];
                    }
                },
                valueFormatter: params => {
                    if (this.isCXOne) {
                        return params.value;
                    } else {
                        if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey)) {
                            return this.getLocaleText("Currently_Open", "Currently Open");
                        }
                        if (!params.value) {
                            return this.isCXOne ? "" : "-";
                        }
                        return this.formatDate(params.value);
                    }
                },
                menuTabs: this.isCXOne ? null : [],
                sort: this.isCXOne && this.favoritesFilter ? 'desc' : null,
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 2,
            },
            {
                field: "LastModifiedDate",
                colId: "LastModifiedDate",
                headerName: this.getLocaleText("Last_Modified", "Last Modified"),
                comparator: this.isCXOne ? (dateA, dateB, nodeA, nodeB) => this.compareDateNullLast(dateA, dateB, nodeA, nodeB, 'LastModifiedDate') : this.compareDateNullFirst,
                cellStyle: params => {
                    if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey))
                        return { 'font-style': 'italic' };
                },
                valueFormatter: params => {
                    if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey))
                        return this.getLocaleText("Currently_Open", "Currently Open");
                    if (!params.value)
                        return "-";
                    return this.formatDate(params.value);
                },
                menuTabs: [],
                flex: 2,
            },
            {
                field: "LastModifiedDate",
                colId: "Modified",
                headerName: this.getLocaleText("Modified", "Modified"),
                comparator: this.isCXOne ? (dateA, dateB, nodeA, nodeB) => this.compareDateNullLast(dateA, dateB, nodeA, nodeB, 'LastModifiedDate') : this.compareDateNullFirst,
                cellStyle: params => {
                    if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey))
                        return { 'font-style': 'italic' };
                },
                valueGetter: params => {
                    if (this.isCXOne) {
                        if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey)) {
                            return this.getLocaleText("Currently_Open", "Currently Open");
                        }
                        if (!params.data || !params.data[params.colDef.field]) {
                            return "-";
                        }
                        return this.formatDate(params.data[params.colDef.field]);
                    } else {
                        return params.data[params.colDef.field];
                    }
                },
                valueFormatter: params => {
                    if (this.isCXOne) {
                        return params.value;
                    } else {
                        if (this.showCurrentlyOpen && this.isDashboardOpen(params.data.DashboardKey))
                            return this.getLocaleText("Currently_Open", "Currently Open");
                        if (!params.value)
                            return "-";
                        return this.formatDate(params.value);
                    }
                },
                menuTabs: this.isCXOne ? null : [],
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 2,
            },
            {
                field: "Favorite",
                colId: "Favorite",
                headerName: this.getLocaleText("Favorite", "Favorite"),
                cellRenderer: 'favoriteButtonRenderer',
                valueFormatter: params => {
                    if (this.dashboardsIsAbandoned.get(params.data.DashboardKey) && params.data.RelatedDashboardID === 0)
                        params.data.IsFavorite = false;
                },
                valueGetter: params => params.data.IsFavorite,
                menuTabs: [],
                minWidth: this.isCXOne ? 85 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 1,
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                lockVisible: true,
            },
            {
                field: "NumRecipientLogins",
                colId: "Shared",
                headerName: this.getLocaleText("Shared_With", "Shared With"),
                cellRenderer: 'shareTextRenderer',
                valueGetter: params => this.getShareColumnText(params.data),
                menuTabs: this.isCXOne ? null : [],
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 2,
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
            },
            {
                field: "Owners",
                colId: "Owners",
                headerName: this.getLocaleText("Owners", "Owners"),
                cellRenderer: 'ownerCellRenderer',
                menuTabs: this.isCXOne ? null : [],
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "CreatedBy",
                colId: "CreatedBy",
                headerName: this.getLocaleText("Created_By", "Created By"),
                menuTabs: this.isCXOne ? null : [],
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 2,
            },
            {
                field: "DashboardDescription",
                colId: "DashboardDescription",
                headerName: this.getLocaleText("Description", "Description"),
                cellRenderer: 'descriptionCellRenderer',
                menuTabs: this.isCXOne ? null : [],
                minWidth: this.isCXOne ? 80 : null,
                maxWidth: this.isCXOne ? 300 : null,
                flex: 3,
            },
            {
                field: "Actions",
                colId: "Actions",
                headerName: this.getLocaleText("Actions", "Actions"),
                cellRenderer: 'actionsCellRenderer',
                menuTabs: [],
                sortable: false,
                minWidth: this.isCXOne ? 100 : null,
                maxWidth: this.isCXOne ? 100 : null,
                flex: 1,
                resizable: this.isCXOne ? false : null,
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    // this behavior was buggy for the second button in the cell, so use the grid's events if it's focused
                    if ((params.event.key === "Enter" || params.event.code === "Enter" || params.event.key === "Tab" ||
                        params.event.code === "Tab") && !document.activeElement.classList.contains("contextMenuButton")) {
                        return true;
                    }
                    else
                        return false;
                },
                lockVisible: true,
            }
        ];

        const selectedColDefs = [];

        this.columns.forEach((column) => {
            const selectedColumn = availableColumnDefs.find(o => o.colId === column);
            if (selectedColumn !== undefined) {
                if (this.allCapsHeaders)
                    selectedColumn.headerName = selectedColumn.headerName.toUpperCase();
                selectedColDefs.push(selectedColumn);
            }
        });

        return selectedColDefs;
    }

    initializeContextMenu(params: GetContextMenuItemsParams) {
        const isAbandoned = this.dashboardsIsAbandoned.get(params.node.data.DashboardKey);
        let contextMenu = [];

        // assign context menus based on access and ownership status
        if (this.isCXOne) {
            if (this.hasCreatePermission) {
                if (this.hasWriteAccess(params.node.data)) {
                    contextMenu = [
                        {
                            name: this.getLocaleText("Edit_Details", "Edit Details"),
                            action: () => this.editDetailsClicked.emit(this.selectedDashboards[0]),
                            icon:
                                `<svg class="icon-16 fill-iconDefault-noHover">
                                    <use href="../App_Themes/CXone/icons/edit.svg#edit"></use>
                                </svg>`,
                            cssClasses: ['isCXoneMenu']
                        },
                        {
                            name: this.getLocaleText("Duplicate", "Duplicate"),
                            action: () => this.copyClicked.emit(),
                            icon:
                                `<svg class="icon-16 fill-iconDefault-noHover">
                                    <use href="../App_Themes/CXone/icons/duplicate.svg#duplicate"></use>
                                </svg>`,
                            cssClasses: ['isCXoneMenu']
                        },
                        {
                            name: this.getLocaleText("Share", "Share"),
                            action: () => this.shareClicked.emit([...this.selectedDashboards]),
                            icon:
                                `<svg class="icon-16 fill-iconDefault-noHover">
                                    <use href="../App_Themes/CXone/icons/share.svg#share"></use>
                                </svg>`,
                            cssClasses: this.isCXOne ? ['isCXoneMenu'] : null
                        },
                    ];

                    if (this.subscriptionsFeatureEnabled) {
                        contextMenu.push(this.getSubscriptionContextMenuOption());
                    }
                }
                else {
                    contextMenu = [
                        {
                            name: this.getLocaleText("Duplicate", "Duplicate"),
                            action: () => this.copyClicked.emit(),
                            icon:
                                `<svg class="icon-16 fill-iconDefault-noHover">
                                    <use href="../App_Themes/CXone/icons/duplicate.svg#duplicate"></use>
                                </svg>`,
                            cssClasses: ['isCXoneMenu']
                        }
                    ];

                    if (this.subscriptionsFeatureEnabled) {
                        contextMenu.push(this.getSubscriptionContextMenuOption());
                    }
                }
            }
            else if (this.hasReadAccess(params.node.data) && this.hasReadPermission) {
                if (this.subscriptionsFeatureEnabled) {
                    contextMenu = [
                        this.getSubscriptionContextMenuOption()
                    ];
                }
            }

            if (isAbandoned && this.hasTakeOwnershipPermission)
                contextMenu.push({
                    name: this.getLocaleText("Take_Ownership", "Take Ownership"),
                    action: async () => {
                        if (params.node.data.RelatedDashboardID > 0)
                            await this.takeOwnership(params.node.data.RelatedDashboardID);
                        else
                            await this.takeOwnership(params.node.data.DashboardID);
                    },
                    icon: `<svg class="icon-16 fill-iconDefault">
                            <use href="../App_Themes/CXone/icons/user.svg#user"></use>
                        </svg>`,
                    cssClasses: ['isCXoneMenu']
                });

            if (contextMenu.length === 0)
                return [
                    {
                        name: this.getLocaleText("No_Actions", "No actions available")
                    }
                ];
            else
                return contextMenu;
        }
        else {
            this.selectedDashboards = [params.node.data];
            this.updateActionsStatus();

            contextMenu.push({
                name: this.getLocaleText("Open_Home", "Open at Home"),
                action: () => this.openSelectedDashboards(),
                icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/open-home.svg#open-home"></use>
                    </svg>`,
                disabled: !this.canOpenSelected
            });

            if (this.dashboardService.permissions["DashboardWindow"]) {
                contextMenu.push({
                    name: this.getLocaleText("Open_Browser", "Open Via Browser"),
                    action: () => { window.open(`${_BaseURI}/Dashboard/DashboardWindow.aspx?id=${params.node.data.DashboardKey}`); },
                    icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/open-browser.svg#open-browser"></use>
                    </svg>`,
                    tooltip: this.getLocaleText("Open_Browser_Tooltip", "This will open as a read-only dashboard on a different window."),
                    shortcut: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/support.svg#support"></use>
                    </svg>`,
                });
            }

            contextMenu.push(
                'separator',
                {
                    name: this.cvLocalizationService.getCachedText("Common", "Move", "Move"),
                    action: () => this.showMoveDialog(),
                    icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/move.svg#move"></use>
                    </svg>`,
                    disabled: !this.canMoveSelected || !this.allowManagement,
                },
                {
                    name: this.cvLocalizationService.getCachedText("Common", "Copy", "Copy"),
                    action: () => this.showCopyDialog(),
                    icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/copy.svg#copy"></use>
                    </svg>`,
                    disabled: !this.allowManagement || !this.hasCreatePermission,
                },
                {
                    name: this.cvLocalizationService.getCachedText("Common", "Delete", "Delete"),
                    action: () => this.confirmDashboardDelete(),
                    icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/delete.svg#delete"></use>
                    </svg>`,
                    disabled: !this.canDeleteSelected || !this.allowManagement,
                },
                {
                    name: this.cvLocalizationService.getCachedText("Common", "Share", "Share"),
                    action: () => this.shareSelectedDashboards(),
                    icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/share.svg#share"></use>
                    </svg>`,
                    disabled: !this.canShareSelected || !this.allowManagement,
                }
            );

            if (this.dashboardService.permissions["ShareViaURL"]) {
                contextMenu.push(
                    {
                        name: 'Share URL',
                        action: () => this.shareURL(params.node.data),
                        icon: `<svg class="icon-12 fill-valyrianSteel">
                            <use href="../App_Themes/clearview-default/icons/utility/link.svg#link"></use>
                        </svg>`,
                        disabled: !this.canShareSelected || !this.allowManagement,
                    }
                );
            }

            contextMenu.push(
                {
                    name: this.cvLocalizationService.getCachedText("Common", "Subscribe", "Subscribe"),
                    action: () => this.showSubscribeDialog(),
                    icon: `<svg class="icon-12 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/subscribe.svg#subscribe"></use>
                    </svg>`,
                    disabled: !this.canSubscribeSelected || !this.allowManagement,
                },
            );

            if (this.hasCreateSystemTemplatePermission && this.onMainDashboardPage && this.canCreateSystemTemplates) {
                contextMenu.push(
                    {
                        name: this.cvLocalizationService.getCachedText("Common", "System", "System"),
                        action: () => this.createSystemTemplate(),
                        icon: `<svg class="icon-12 fill-valyrianSteel">
                            <use href="../App_Themes/clearview-default/icons/utility/template.svg#template"></use>
                        </svg>`,
                    }
                );
            }
            return contextMenu;
        }
    }

    updateSort() {
        if (this.isCXOne && this.gridOptions && !this.dashboardService.dashboardsLoading) {
            if (this.favoritesFilter) {
                this.agGrid?.api.applyColumnState({
                    state: [
                        {
                            colId: 'LastViewDate',
                            sort: 'desc',
                            sortIndex: 0,
                        },
                        {
                            colId: 'DashboardName',
                            sort: 'asc',
                            sortIndex: 1,
                        }
                    ]
                });
            }
            else {
                this.agGrid?.api.applyColumnState({
                    state: [
                        {
                            colId: 'DashboardName',
                            sort: 'asc'
                        },
                        {
                            colId: 'LastViewDate',
                            sort: null
                        }
                    ]
                });
            }
        }
    }

    updateNoRowsMessage() {
        if (this.isCXOne) {
            // -- no access
            if (!this.hasReadPermission) {
                this.gridOptions.overlayNoRowsTemplate =    `<div>
                    <svg class="cxoneFilterState mb-2">
                        <use href="../App_Themes/CXone/icons/no-permission.svg#no-permission"></use>
                    </svg>
                    <p class="cxoneEmptyText">${this.cvLocalizationService.getCachedText("Dashboards_Landing", "No_Management_Permission", "You don't have permission to manage dashboards")}</p>
                </div>`;
            }
            // -- no data
            else if (this.dashboardService.loadExistingDashboardsFailed) {
                this.gridOptions.overlayNoRowsTemplate = `<div>
                    <svg class="cxoneErrorState mb-2">
                        <use href="../App_Themes/CXone/icons/data-error.svg#data-error"></use>
                    </svg>
                    <p class="cxoneEmptyText">
                        ${this.cvLocalizationService.getCachedText("Dashboards_Landing", "Load_Error", "Cannot load data")}
                    </p>
                </div>`;
            }
            // -- no favorites and search results
            else if (this.favoritesFilter && this.searchFilter.length > 0) {
                this.gridOptions.overlayNoRowsTemplate = `<div>
                    <svg class="cxoneFavoriteState mb-2" style="padding-top: 32px;">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxoneEmptyText">
                        ${this.cvLocalizationService.getCachedText("Dashboards_Landing", "No_Results", "No results found")}
                    </p>
                </div>`;
            }
            // -- no favorites
            else if (this.favoritesFilter) {
                this.gridOptions.overlayNoRowsTemplate = `<div>
                    <svg class="cxoneFavoriteState mb-2">
                        <use href="../App_Themes/CXone/icons/add-dashboard.svg#add-dashboard"></use>
                    </svg>
                    <p class="cxoneEmptyText">
                        ${this.cvLocalizationService.getCachedText("Dashboards_Landing", "No_Dashboards_Added", "No dashboards added yet")}
                    </p>
                </div>`;
            }
            // -- no search results
            else if (this.searchFilter.length > 0) {
                this.gridOptions.overlayNoRowsTemplate = `<div>
                    <svg class="cxoneFilterState mb-2" style="padding-top: 32px;">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxoneEmptyText">
                        ${this.cvLocalizationService.getCachedText("Dashboards_Landing", "No_Results", "No results found")}
                    </p>
                </div>`;
            }
            // -- no dashboards
            else {
                this.gridOptions.overlayNoRowsTemplate = `<div>
                    <svg class="cxoneEmptyState mb-2">
                        <use href="../App_Themes/CXone/icons/add-dashboard.svg#add-dashboard"></use>
                    </svg>
                    <p class="cxoneEmptyText">
                        ${this.cvLocalizationService.getCachedText("Dashboards_Landing", "No_Dashboards_Added", "No dashboards added yet")}
                    </p>
                </div>`;
            }
        } // if not CXOne, the error message should stay the default
    }

    updateManagementState() {
        if (!this.allowManagement) {
            const managementErrorText = this.getLocaleText(
                "Management_Tooltip",
                "Cannot make modifications to dashboards in this view."
            );
            this.moveSelectedTooltip = managementErrorText;
            this.copySelectedTooltip = managementErrorText;
            this.deleteSelectedTooltip = managementErrorText;
            this.shareSelectedTooltip = managementErrorText;
            this.subscribeSelectedTooltip = managementErrorText;
        } else {
            this.moveSelectedTooltip = '';
            this.deleteSelectedTooltip = '';
            this.shareSelectedTooltip = '';
            this.subscribeSelectedTooltip = '';

            if (this.hasCreatePermission) {
                this.copySelectedTooltip = '';
            }

            this.updateActionsStatus();
        }
    }

    async activateAndNavigateToDashboard(dashboardKey: string) {
        // dashboard is open already, set as active
        if (this.dashboardService.activeDashboard.key !== dashboardKey) {
            this.dashboardService.triggerActiveDashboardChanged(dashboardKey, this.dashboardService.activeDashboard.key);
            this.dashboardService.activeDashboard.key = dashboardKey;
        }

        this.navigationService.selectedPageGroup = false;
        await this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, dashboardKey, true);

        if (this.isCXOne) {
            sendMessageCXOne({dashboardSelected: true});
            this.dashboardNavigationService.navigateToCXOneDashboardView();
        }
        else
            this.dashboardNavigationService.navigateToDashboardView();
    }

    showMoveDialog() {
        this.moveDashboardDropdown.open();
    }

    async moveDashboards() {
        if (this.isMoving)
            return;

        this.isMoving = true;
        const groupNameLower = this.moveDashboardDestination?.toLowerCase();
        if (!groupNameLower) {
            window['showInfoDialog'](this.getLocaleText(
                "Error_Move_No_Name",
                "Please select a group from the dropdown or type a new group name."
            ), "Warning");
        } else if (groupNameLower === "shared with me") {
            window['showInfoDialog'](this.getLocaleText(
                "Error_Move_Shared",
                "This folder is restricted for Shared Dashboards only. Please select a different folder."
            ), "Warning");
        } else if (groupNameLower === "taskbar") {
            window['showInfoDialog'](this.getLocaleText(
                "Error_Move_Taskbar",
                '"Taskbar" is a reserved group name. Please use a different group name.'
            ), "Warning");
        } else {
            try {
                const selectedDashboards = [...this.selectedDashboards];
                await this.dashboardService.moveDashboards(this.selectedDashboards.map(d => d.DashboardKey), this.moveDashboardDestination);
                for (const dashboard of selectedDashboards) {
                    dashboard.DashboardGroup = this.moveDashboardDestination;
                    if (this.agGrid?.api) {
                        const dashboardRowNode = this.agGrid?.api.getRowNode(dashboard.DashboardKey);
                        dashboardRowNode.setData(dashboard);
                    }
                }
            } catch (err) {
                window['showInfoDialog'](err, "Error");
            }
            this.moveDashboardDropdown.close();
        }

        this.moveDashboardDestination = "";
        this.isMoving = false;
    }

    showCopyDialog() {
        this.copyClicked.emit(this.selectedDashboards[0]);
    }

    async confirmDashboardDelete() {
        await this.cvLocalizationService.GetSection("Confirmation_Popup");
        const getConfirmText = (key: string, defVal: string) => this.cvLocalizationService.getCachedText("Confirmation_Popup", key, defVal);

        if (this.isCXOne) {
            const modalRef = this.modalService.open(this.cxoneDeleteModal);
            modalRef.componentInstance.result.then(() => this.deleteSelectedDashboards());
        }
        else {
            let isShared = false;
            let isSubscribed = false;
            let prompt = getConfirmText("Base_Copy", "Are you sure you want to delete");

            for (const dashboard of this.selectedDashboards) {
                // Was shared with You
                if (dashboard.SharedBy && !this.isCXOne) { // TODO: Verify base behavior
                    // this.navigation.confirmationPopup.content.push("notOwner");
                    // this.navigation.confirmationPopup.type = 'NDelete';
                    // this.navigation.confirmationPopup.showPopup = true;
                    return;
                }

                // Is being shared
                if (dashboard.NumRecipientLogins > 0)
                    isShared = true;

                // Has Subscriptions
                if (dashboard.IsSubscribed)
                    isSubscribed = true;
            }

            if (this.selectedDashboards.length > 1)
                prompt += getConfirmText("Dashboards", " these dashboards");
            else
                prompt += getConfirmText("Dashboard", " this dashboard");

            if (isShared)
                prompt += getConfirmText("Shares", " and all Shares");

            if (isSubscribed)
                prompt += getConfirmText("And_Subscriptions", " and all attached subscriptions");

            prompt += "?";

            this.dialogService.showConfirmDialog(
                this.cvLocalizationService.getCachedText("Common", "Confirm_Delete", "Confirm Delete"),
                prompt,
                this.isCXOne,
                () => this.deleteSelectedDashboards(),
            );
        }
    }

    async deleteSelectedDashboards() {
        const keys = this.selectedDashboards.map(d => d.DashboardKey);
        const keysSet = new Set(keys);
        await this.dashboardService.deleteDashboards(keys, this.isCXOne);

        // update dashboard list
        this.dashboardService.availableDashboards = this.dashboardService.availableDashboards.filter(d => !keysSet.has(d.DashboardKey));

        // update open dashboards
        if (this.dashboardService.dashboards?.length > 0 && this.dashboardService.dashboards[0] !== null) {
            this.dashboardService.dashboards = this.dashboardService.dashboards.filter(d => !keysSet.has(d.DashboardKey));
            if (keysSet.has(this.dashboardService.activeDashboard.key)) {
                if (this.dashboardService.dashboards.length > 0) {
                    const index = this.dashboardService.dashboards.length - 1;
                    this.dashboardService.activeDashboard.key = this.dashboardService.dashboards[index].DashboardKey;
                } else {
                    this.dashboardService.activeDashboard.key = null;
                    this.dashboardService.dashboards.push(null);
                }
            }
        }
    }

    async setFavorite(dashboardID: number, value: boolean) {
        const data = await this.dashboardService.setFavorite(dashboardID, value);
        const index = this.dashboards.findIndex(d => d.DashboardID === dashboardID);
        this.dashboards[index].IsFavorite = data.IsFavorite;

        if (this.agGrid?.api) {
            this.agGrid?.api.refreshCells();
            this.applyFilter();
        }

        return data;
    }

    shareSelectedDashboards() {
        this.shareClicked.emit([...this.selectedDashboards]);
    }

    showSubscribeDialog() {
        this.subscribeClicked.emit([...this.selectedDashboards]);
    }

    createSystemTemplate() {
        if (!this.hasCreateSystemTemplatePermission)
            return;

        // Close any new tabs
        for (let i = this.dashboardService.dashboards.length - 1; i >= 0; i--) {
            if (this.dashboardService.dashboards[i] == null) {
                this.dashboardService.dashboards.splice(i, 1);
                break;
            }
        }

        // Use the "New Tab" page to configure the new system dashboard names.
        const dashboards = this.dashboardService.dashboards;
        if (dashboards.length === 0 || dashboards[dashboards.length - 1] != null)
            dashboards.push(null);

        const prevDashboardKey = this.dashboardService.activeDashboard ? this.dashboardService.activeDashboard.key : null;
        this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, null, true);
        this.dashboardService.triggerActiveDashboardChanged(null, prevDashboardKey);

        const selectedDashboard = this.selectedDashboards[0];
        const templateInfo = this.systemTemplateService.systemTemplateInfo;

        templateInfo.dashboardKey = selectedDashboard.DashboardKey;
        templateInfo.group = selectedDashboard.DashboardGroup;
        templateInfo.name = selectedDashboard.DashboardName;

        this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Templates;
        this.dashboardService.templateView = WorkspaceTemplateViewMode.NewSystemTemplate;
        this.navigationService.selectedPageGroup = false;
    }

    /**
     * Gets a dashboard by its key then calls openDashboards helper method
     */
    async openDashboard(dashboardKey: string) {
        if (this.openDashboardIndices.has(dashboardKey)) {
            await this.activateAndNavigateToDashboard(dashboardKey);
        } else if (this.canOpenNewDashboard() && !this.isLoading) {
            this.isLoading = true;
            const index = this.dashboardIndices.get(dashboardKey);
            await this.openDashboards(this.dashboards[index]);
        }
    }

    async openSelectedDashboards() {
        if (this.selectedDashboards.length <= 0)
            return;

        const dashboardsToOpen = this.selectedDashboards.filter(row => !this.openDashboardIndices.has(row.DashboardKey));

        if (dashboardsToOpen.length > 0) {
            this.openDashboards(...dashboardsToOpen);
        } else {
            // all dashboards open already, set last one in selection to active
            const dashboardKey = this.selectedDashboards[this.selectedDashboards.length - 1].DashboardKey;
            await this.activateAndNavigateToDashboard(dashboardKey);
        }
    }

    async openDashboards(...dashboards: DashboardResponsiveApiModelSparse[]) {
        try {
            if (this.isCXOne) {
                const workspaceKey = this.dashboardService.workspaceKey;
                await this.dashboardService.clearWorkspace(workspaceKey, true);
                this.dashboardService.clearDashboardsFromWorkspace();
                await this.dashboardService.addDashboardToWorkspace(workspaceKey, dashboards[0], true, true);
                this.dashboardNavigationService.navigateToCXOneDashboardView();
            }
            else {
                // set the dashboards as open in the state
                await this.dashboardService.openDashboards(dashboards.map(d => d.DashboardKey));

                // close new tab
                for (let i = this.dashboardService.dashboards.length; i >= 0; i--) {
                    if (this.dashboardService.dashboards[i] === null) {
                        this.dashboardService.dashboards.splice(i, 1);
                        this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Landing;
                        break;
                    }
                }

                // open dashboards
                for (const dashboard of dashboards) {
                    this.openDashboardIndices.set(dashboard.DashboardKey, this.dashboardService.dashboards.length);
                    this.dashboardService.dashboards.push(dashboard);
                }

                if (!this.dashboardNavigationService.navigateToDashboardView()) {
                    this.dashboardService.activeDashboard.key = dashboards[dashboards.length - 1].DashboardKey;
                    this.navigationService.selectedPageGroup = false;
                    await this.dashboardService.setActiveDashboard(this.dashboardService.workspaceKey, this.dashboardService.activeDashboard.key, true);
                }
            }
        } finally {
            this.isLoading = false;
        }
    }

    updateActionsStatus() {
        if (this.selectedDashboards.length === 0) {
            this.canMoveSelected = false;
            this.canDeleteSelected = false;
            this.canShareSelected = false;
            this.canSubscribeSelected = false;
            this.canOpenSelected = false;
            this.openSelectedTooltip = '';

            if (this.allowManagement) {
                this.moveSelectedTooltip = '';
                this.deleteSelectedTooltip = '';
                this.shareSelectedTooltip = '';
                this.subscribeSelectedTooltip = '';
            }
            return;
        }

        this.canOpenSelected = false;
        this.openSelectedTooltip = '';
        const selectedUnopenedDashboards = this.selectedDashboards.filter(row => !this.openDashboardIndices.has(row.DashboardKey));
        const openDashboards = this.dashboardService.dashboards.filter(d => d != null);
        if (selectedUnopenedDashboards.length > 0) {
            if ((selectedUnopenedDashboards.length + openDashboards.length) <= this.dashboardService.maxAttachedDashboards) {
                this.canOpenSelected = true;
            } else {
                const defaultText = 'You already have {0} of the {1} allotted tabs open on your home page.'
                    + ' You can only open {2} more dashboard.';
                const tooltipText = this.getLocaleText('Open_Tooltip', defaultText);
                this.openSelectedTooltip = this.cvLocalizationService.Format(
                    tooltipText,
                    openDashboards.length,
                    this.dashboardService.maxAttachedDashboards,
                    this.dashboardService.maxAttachedDashboards - openDashboards.length
                );
            }
        }

        if (this.selectedDashboards.length === 1) {
            if (openDashboards.length < this.dashboardService.maxAttachedDashboards) {
                this.canCreateSystemTemplates = true;
                this.systemTemplateTooltip = '';
            } else {
                this.canCreateSystemTemplates = false;
                this.systemTemplateTooltip = this.cvLocalizationService.Format(
                    this.getLocaleText('System_Full_Tooltip', `You already have {0} of the {1} allotted tabs open on you home page.`),
                    openDashboards.length, this.dashboardService.maxAttachedDashboards
                );
            }
        } else {
            this.canCreateSystemTemplates = false;
            this.systemTemplateTooltip = this.getLocaleText(
                'System_Tooltip',
                'Cannot create a system template for more than one dashboard at a time.'
            );
        }

        if (this.allowManagement) {
            if (this.selectedDashboards.length === 1) {
                if (this.hasCreatePermission)
                    this.copySelectedTooltip = '';

                const sel = this.dashboards.find(d => d.DashboardKey === this.selectedDashboards[0].DashboardKey);
                if (!sel.SharedBy || sel.SharedWriteAccess) {
                    this.canSubscribeSelected = true;
                    this.shareSelectedTooltip = '';
                } else {
                    this.canSubscribeSelected = false;
                    this.subscribeSelectedTooltip = this.getLocaleText(
                        "Scheduler_Tooltip_Access",
                        "You do not have permission to subscribe to this dashboard."
                    );
                }
            } else {
                this.canSubscribeSelected = false;
                this.copySelectedTooltip = this.getLocaleText("Copy_Tooltip_Multiple", "You can only copy one dashboard at a time.");
                this.subscribeSelectedTooltip = this.getLocaleText(
                    "Scheduler_Tooltip_Multiple",
                    "You can only schedule a subscription on one dashboard at a time."
                );
            }
        }

        this.canMoveSelected = true;
        this.canDeleteSelected = true;
        this.canShareSelected = true;

        for (const dashboard of this.selectedDashboards) {
            if (dashboard.SharedBy) {
                this.canMoveSelected = false;
                this.canDeleteSelected = false;

                if (!dashboard.SharedWriteAccess) {
                    this.canShareSelected = false;
                    if (this.allowManagement) {
                        this.shareSelectedTooltip = this.getLocaleText(
                            "Share_Tooltip",
                            "You do not have access to share one or more of the selected dashboards."
                        );
                    }
                }

                if (this.allowManagement) {
                    this.moveSelectedTooltip = this.getLocaleText(
                        "Move_Tooltip_Access",
                        "You do not have access to move one or more of the selected dashboards."
                    );
                    this.deleteSelectedTooltip = this.getLocaleText(
                        "Delete_Tooltip_Access",
                        "You do not have access to delete one or more of the selected dashboards."
                    );
                }
            }
        }
    }

    applyFilter() {
        this.agGrid?.api?.onFilterChanged();
    }

    clearFilter() {
        this.searchFilter = "";
        this.agGrid?.api?.hideOverlay();
        this.applyFilter();
    }

    handleDashboardFilterClick(index: number) {
        if(index === this.selectedDashboardFilter)
            return;

        this.selectedDashboardFilter = index;
        this.applyFilter();
    }

    doesDashboardFilterPass(params){
        if(this.selectedDashboardFilter === 1)
            return params.data.CreatorID === this.navigationService.loggedInID && params.data.NumRecipientLogins > 0;

        if(this.selectedDashboardFilter === 2)
            return params.data.CreatorID !== this.navigationService.loggedInID && params.data.NumRecipientLogins > 0;

        return false;
    }

    setGridData() {
        if (!this.hasReadPermission && this.isCXOne) {
            this.updateNoRowsMessage();
            this.agGrid?.api.showNoRowsOverlay();
            this.agGrid?.api.setGridOption('rowData', []);
            return;
        }

        if (this.dashboardService.dashboardsLoading) {
            this.agGrid?.api.showLoadingOverlay();
            return;
        }

        this.agGrid?.api.hideOverlay();
        if (!this.isCXOne)
            this.agGrid?.api.sizeColumnsToFit();

        if (this.dashboards) {
            const prevSelected = this.selectedDashboards;
            this.agGrid?.api.setGridOption('rowData', this.dashboards);

            for (const selected of prevSelected) {
                const rowNode = this.agGrid?.api.getRowNode(selected.DashboardKey);
                rowNode?.setSelected(true);
            }

            this.selectedDashboards = this.agGrid?.api.getSelectedRows();
            this.selectionChanged.emit([...this.selectedDashboards]);
            this.updateActionsStatus();
        }

        if (this.isCXOne)
            this.updateSort();

        this.applyFilter();
        this.agGrid?.api.refreshCells();
        this.isLoading = false;
    }

    hasWriteAccess(dashboard: DashboardResponsiveApiModelSparse): boolean {
        if ((dashboard.RelatedDashboardID === 0 && !this.dashboardsIsAbandoned.get(dashboard.DashboardKey)) || dashboard.SharedWriteAccess)
            return true;
        else
            return false;
    }

    hasReadAccess(dashboard: DashboardResponsiveApiModelSparse): boolean {
        if (dashboard.SharedReadAccess || dashboard.SharedPublicAccess || this.hasWriteAccess(dashboard))
            return true;
        else
            return false;
    }

    isDashboardOpen(dashboardKey: string) {
        return this.openDashboardIndices.has(dashboardKey);
    }

    private sanitizeName(name: string) {
        return name?.replace(/</gi, "&#60;").replace(/=/gi, "&#61;").replace(/>/gi, "&#62;");
    }

    private formatDate(dateString: string) {
        let date = dateString;
        date = date.substring(0, 19); // length of ISO datetime string without time zone info
        if (this.isCXOne) {
            const dateObj = new Date(date);
            const dateDifference = dateObj.getElapsed(Date.today()) / (60*60*24*1000);
            if (dateDifference >= 7)
                return formatDate(date, 'mediumDate', this.locale);
            else if (dateDifference > 1) {
                const DOW: string = formatDate(date, "EEEE", this.locale);
                return `${this.getLocaleText(DOW, DOW)} ${formatDate(date, "hh:mm a", this.locale)}`;
            }
            else if (dateDifference > 0)
                return `${this.getLocaleText("Yesterday", "Yesterday")} ${formatDate(date, "hh:mm a", this.locale)}`;
            else
                return `${this.getLocaleText("Today", "Today")} ${formatDate(date, "hh:mm a", this.locale)}`;
        }
        else
            return formatDate(date, CVAngularDateFormat, this.locale);
    }

    /** Compare dateA and dateB and returns the result, considers null date more recent */
    private compareDateNullFirst(dateA, dateB): number {
        if (!dateA && !dateB)
            return 0;
        else if (!dateA && dateB)
            return 1;
        else if (dateA && !dateB)
            return -1;
        return new Date(dateA).compareTo(new Date(dateB));
    }

    /** Compare nodeA and nodeB to obtain the raw date and returns the result, considers null date less recent */
    private compareDateNullLast(dateA, dateB, nodeA, nodeB, field: string): number {
        dateA = nodeA.data?.[field];
        dateB = nodeB.data?.[field];

        if (!dateA && !dateB)
            return 0;
        else if (!dateA && dateB)
            return -1;
        else if (dateA && !dateB)
            return 1;
        return new Date(dateA).compareTo(new Date(dateB));
    }

    private getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Dashboards_Landing", key, defaultValue);
    }

    /** Gets the text for the share column. CXOne uses 'teams' and 'employees'. */
    private getShareColumnText(dashboard): string {
        const isAbandoned = this.dashboardsIsAbandoned.get(dashboard.DashboardKey);

        if (this.hasWriteAccess(dashboard) && this.hasCreatePermission) {
            if (dashboard.IsPublic)
                return this.getLocaleText("All_Employees", "All employees");

            // get number of employees and teams shared to
            let shares = '';
            const teamCount = dashboard.RecipientGroups ? Object.keys(dashboard.RecipientGroups)?.length : 0;
            const employeeCount = dashboard.RecipientLogins ? dashboard.RecipientLogins.length : 0;
            if (teamCount > 0) {
                let teamText = teamCount === 1 ? this.getLocaleText("Teams_Singular", "{0} team")
                    : this.getLocaleText("Teams_Plural", "{0} teams");
                teamText = this.cvLocalizationService.Format(teamText, teamCount);
                shares += teamText;
                if (employeeCount > 0)
                    shares += ', ';
            }
            if (employeeCount > 0) {
                let employeeText = employeeCount === 1 ? this.getLocaleText("Employees_Singular", "{0} employee")
                    : this.getLocaleText("Employees_Plural", "{0} employees");
                employeeText = this.cvLocalizationService.Format(employeeText, employeeCount);
                shares += employeeText;
            }

            return shares;
        }
        else {
            if ((this.hasReadAccess(dashboard) && (dashboard.RecipientGroups || dashboard.RecipientLogins?.length > 1
                || dashboard.SharedPublicAccess)) || (isAbandoned && dashboard.IsPublic))
                return this.getLocaleText("Me_And_Others", "Me and others");
            else
                return this.getLocaleText("Me", "Me");
        }
    }

    private async takeOwnership(dashboardId: number) {
        try {
            await this.takeOwnershipService.takeOwnership(dashboardId);
            this.dashboardService.loadExistingDashboardsAndGroups();
        }
        catch (err) {
            if (err.status_code === 405)
                this.modalService.open(
                    this.ownershipErrorModal,
                    { centered: true, windowClass: 'cxone-error-content', modalDialogClass: 'cxone-error-dialog' }
                );
        }
    }

    private canOpenNewDashboard(): boolean{
        const dashboardOpened = this.dashboardService.dashboards.filter(d => d != null);
        return dashboardOpened.length < this.dashboardService.maxAttachedDashboards;
    }

    private shareURL(dashboard: DashboardResponsiveApiModelSparse) {
        const options = {
            dashboard,
        };
        this.shareViaURL.openModal(options, ShareViaUrlComponent);
    }

    /** Creates the subscription context menu option. Wrapped in a function instead of directly stored as an object so the name (and thus localization) is lazily loaded */
    private getSubscriptionContextMenuOption = () => ({
        name: this.getLocaleText("Subscribe", "Subscribe"),
        action: () => this.subscribeClicked.emit(),
        icon: this.isCXOne ?
            `<svg class="icon-16 fill-iconDefault">
                    <use href="../App_Themes/CXone/icons/email.svg#email"></use>
                </svg>` : `<svg class="icon-16 fill-valyrianSteel">
                    <use href="../App_Themes/clearview-default/icons/utility/subscribe.svg#subscribe"></use>
                </svg>`,
        cssClasses: this.isCXOne ? ['isCXoneMenu'] : null
    });
}

interface ContextUtil {
    hasWriteAccess: (dashboard: DashboardResponsiveApiModelSparse) => boolean;
    hasReadAccess: (dashboard: DashboardResponsiveApiModelSparse) => boolean;
    isDashboardAbandoned: (dashboardKey: string) => boolean;
    deleteDashboard: () => void;
    shareDashboard: (dashboard: DashboardResponsiveApiModelSparse) => void;
    setFavorite: (dashboardID: number, value: boolean) => Promise<DashboardResponsiveApiModelSparse>;
    getAddFavoriteTooltip: () => string;
    getRemoveFavoriteTooltip: () => string;
    getDeleteTooltip: () => string;
    getFilterText: () => string;
}
